import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { ColumnDef, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import savedSearchStore, { SavedSearchInfo } from "~/stores/savedSearch";
import TabContent from "./TabContent";
import TabTable, { CommonTabProps } from "./TabTable";
import { TITLE_MAX_CHARS, useDataFetcher } from "./common";
import { NavLink } from "react-router-dom";
import paths from "~/paths";
import SearchQuery from "~/searchQuery";
import { Button } from "~/components/ui/button";
import { Trash2 } from "lucide-react";
import { formatDateReadableRange } from "~/util";
import languageStore from "~/stores/language";
import countryStore, { CountryId } from "~/stores/country";
import { flatten } from "lodash/fp";
import notificationStore from "~/stores/notifications";
import Modal from "~/components/Modal";

interface SavedSearchInfoRow extends SavedSearchInfo {
  isBeingDeleted?: boolean;
  pageIndex?: number;
}

const SavedSearchTab = observer((props: CommonTabProps) => {
  const fetcher = useDataFetcher(props.selected, savedSearchStore.f.loadBare, { clientOnlyPagination: true });
  const bookmarks = fetcher.items.results;
  const idsBeingDeleted = savedSearchStore.f.idsBeingDeleted;

  const [searchDeletionModalShown, setSearchDeletionModalShown] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState<SavedSearchInfoRow | null>(null);

  const isDeleting = (id: any) => idsBeingDeleted.has(id);

  const data: SavedSearchInfoRow[] = useMemo(() => {
    return bookmarks.reduce((acc, item) => {
      acc.push({
        ...item,
        isBeingDeleted: isDeleting(item.id),
      });
      return acc;
    }, [] as SavedSearchInfoRow[]);
  }, [bookmarks, idsBeingDeleted]);

  useEffect(() => {
    // preload countries
    const ids = flatten(
      bookmarks.map((a) => a.complex_search_value?.countries).filter((a) => a !== undefined)
    ) as CountryId[];
    countryStore.f.loadIds(ids);
  }, [bookmarks]);

  const columns: ColumnDef<SavedSearchInfoRow, any>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        accessorKey: "description",
        cell: (info) => {
          const doc = info.row.original;
          let title = doc.description;
          title = title.length > TITLE_MAX_CHARS ? `${title.substr(0, TITLE_MAX_CHARS)}...` : title;
          const sq = SearchQuery.fromSavedSearch(doc);
          return (
            <div>
              <NavLink className="text-primary-600 hover:underline" to={paths.construct.browse(sq)}>
                {title}
              </NavLink>
            </div>
          );
        },
      },
      {
        header: "Query",
        accessorKey: "search_value",
      },
      {
        id: "languages",
        header: "Languages",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-1.5">
              {doc.complex_search_value.languages
                ? doc.complex_search_value.languages.map(languageStore.idToText).join(", ")
                : "None"}
            </div>
          );
        },
      },
      {
        id: "countries",
        header: "Countries",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-1.5">
              {doc.complex_search_value.countries && doc.complex_search_value.countries.length !== 0
                ? doc.complex_search_value.countries.map(countryStore.idToText).join(", ")
                : "N/A"}
            </div>
          );
        },
      },
      {
        id: "expires_at",
        header: "Expires at",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-1.5">
              {doc.complex_search_value.expiration_date
                ? formatDateReadableRange(doc.complex_search_value.expiration_date)
                : "N/A"}
            </div>
          );
        },
      },
      {
        id: "release_date",
        header: "Release date",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-1.5">
              {doc.complex_search_value.release_date
                ? formatDateReadableRange(doc.complex_search_value.release_date)
                : "N/A"}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-destructive hover:text-destructive"
                title="Delete the search"
                onClick={() => {
                  setSearchDeletionModalShown(true);
                  setSearchToDelete(doc);
                }}
              >
                <Trash2 className="size-4" />
                Delete
              </Button>
            </div>
          );
        },
      },
    ],
    [data, countryStore.f.items]
  );

  const tableInstance = useReactTable({
    data,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  const [loading, setLoading] = useState(false);

  return (
    <TabContent>
      <Modal
        open={searchDeletionModalShown}
        modalHeading="Delete saved search"
        modalLabel="Saved searches"
        onRequestClose={() => {
          setSearchToDelete(null);
          setSearchDeletionModalShown(false);
        }}
        primaryButtonDisabled={fetcher.loading || savedSearchStore.f.loading}
        primaryButtonText="Confirm"
        secondaryButtonText="Cancel"
        onRequestSubmit={async () => {
          setLoading(true);
          await savedSearchStore.f.delete(searchToDelete.id);
          notificationStore.success("Deleted saved search");
          await fetcher.reload();
          setSearchToDelete(null);
          setSearchDeletionModalShown(false);
          setLoading(false);
        }}
      >
        Do you really want to delete the saved search "{searchToDelete?.description}"?
      </Modal>
      <TabTable
        fetcher={fetcher}
        tableInstance={tableInstance}
        emptyTitle="No search bookmarks"
        emptyMessage="You have zero saved search queries"
        fluid
      />
    </TabContent>
  );
});

export default SavedSearchTab;
